import React, { useEffect, useState } from "react";
import "./App.scss";
import { ConfigProvider } from "./providers/ConfigContext";
import LandingPage from "./pages/LandingPage/LandingPage";
import parseQueryParams from "./utils/parseQueryParams";
import NewPurchaseContent from "./pages/NewPurchase/NewPurchaseContent";
import ProcessRefundContent from "./pages/ProcessRefund/ProcessRefundContent";
import SalesTotalContent from "./pages/SalesTotal/SalesTotalContent";
import PaymentMethodContent from "./pages/PaymentMethod/PaymentMethodContent";
import ErrorPage from "./pages/Error Pages/ErrorPage";
import NotAvailable from "./pages/Error Pages/NotAvailable";
import CreditCardForm from "./pages/CreditCard/CreditCardForm";
import CreditCardFinal from "./pages/CreditCard/CreditCardFinal";
import { CreditCardInfo, defaultCreditCardInfo } from "./interfaces/CreditCardInfo";
import PaymentConfirmationContent from "./pages/Confirmation/PaymentConfirmationContent";
import bookfair from "./interfaces/bookfair-info";
import STFRedeemContent from "./pages/STFRedeem/STFRedeemContent";
import EwalletReviewContent from "./pages/EwalletPages/EwalletContent";
import EwalletDisplayContent from "./pages/EwalletPages/EwalletDisplayContent";
import EwalletChooseTransactionContent from "./pages/EwalletPages/RefundPages/EwalletChooseTransactionContent";
import EwalletRefundReviewContent from "./pages/EwalletPages/RefundPages/EwalletRefundReviewContent";
import { defaultFairBarInfo, FairBarInfo } from "./interfaces/fairBar-info";
import { Builder } from "./pages/PaymentPortalBuilder/Builder";
import { EwalletInfo, defaultEwalletInfo } from "./interfaces/ewallet-info";
import { defaultTransactionInfoTax, TransactionInfoTax } from "./interfaces/transaction-info-tax";
import { Route, Routes } from "react-router-dom";
import RedirectPage from "./pages/Error Pages/RedirectPage";
import { ToastContainer, toast } from "react-toastify";
import PurchaseOrderPaymentContent from "./pages/PurchaseOrder/PurchaseOrderPaymentContent";
import ScholasticDollars from "./pages/ScholasticDollars/ScholasticDollars";
import Checks from "./pages/Checks/Checks";
import CashPaymentContent from "./pages/CashPayment/CashPaymentContent";
import { SaleResponseInfo, defaultSaleResponseInfo } from "./interfaces/sale-response-info";
import BigNumber from "bignumber.js";

const defaultBookfairInfo = {
  school: "",
  stfBalance: BigNumber(0),
  startDate: "",
  endDate: "",
  id: 0,
  isFairEnded: false,
  ccSales: 0,
  ewalletSales: 0,
  totalSales: 0,
  stfSales: 0,
  taxStatus: "",
  taxRate: BigNumber(0),
  taxExemptSalesAllowed: false,
  maxTransactionItems: 0,
  ewalletEnabled: false,
  chairpersonEmail: "",
}

export enum Pages {
  Landing,
  NewPurchase,
  ProcessRefund,
  SalesTotal,
  PaymentMethod,
  CreditCardForm,
  CreditCardReview,
  Confirmation,
  STFRedeem,
  eWallet,
  eWalletReview,
  eWalletRefund,
  eWalletRefundTransactions,
  eWalletRefundReview,
  POPayment,
  CashPayment,
  Error,
  NotAvailable,
  ScholasticDollars,
  Checks
}

function App() {
  const { fairId } = parseQueryParams(window.location);

  const [creditCardInfo, setCreditCardInfo] = React.useState<CreditCardInfo>(defaultCreditCardInfo);
  const [bookfairInfo, setBookfairInfo] = useState<bookfair>(defaultBookfairInfo);
  const [transactionItems, setTransactionItems] = useState([{key: 0, value: ""}, {key: 1, value: ""}, {key: 2, value: ""}, {key: 3, value: ""}]);
  const [isTaxExemptChecked, setIsTaxExemptChecked] = useState(false);
  const [transactionId, setTransactionId] = useState("0");
  const [saleResponseInfo, setSaleResponseInfo] = useState<SaleResponseInfo>(defaultSaleResponseInfo);
  const [defaultConfirmationEmail, setDefaultConfirmationEmail] = useState<string>('');
  const [loggedIn, setLoggedIn] = useState<boolean>(false)
  const [payoffAmount, setPayoffAmount] = useState<BigNumber>(BigNumber(0));
  const [payoffFairId, setPayoffFairId] = useState<number>(0);
  const [refundAmount, setRefundAmount] = useState<BigNumber>(BigNumber(0));
  const [cashAmountPaid, setCashAmountPaid] = useState<BigNumber>(BigNumber(0));
  const [payoffFairInfo, setPayoffFairInfo] = useState<FairBarInfo>(defaultFairBarInfo);
  const [currentPage, setCurrentPage] = useState<number>(Pages.Landing)
  const [selectedEWallet, setSelectedEWallet] = useState<EwalletInfo>(defaultEwalletInfo);
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionInfoTax>(defaultTransactionInfoTax);
  const [currentPagePayoff, setCurrentPagePayoff] = useState<number>(Pages.CreditCardForm)
  const [modalDisplayed, setModalDisplayed] = useState<boolean>(false);

  var GRECAPTCHA = "grecaptcha-badge";

  // Calculate sum of all transaction items
  let itemTotal = new BigNumber(0);
  transactionItems.forEach(({value}) => {
      if (value !== '') {
        itemTotal = itemTotal.plus(value);
      }
  });

  // Calculate tax
  let tax = new BigNumber(0);
  if (!isTaxExemptChecked) {
    tax = itemTotal.times(bookfairInfo.taxRate.div(100)).dp(2);
  }

  // Calculate total amount due
  const amountDue = tax.plus(itemTotal);

  const paymentInfo = {
    itemTotal,
    tax,
    amountDue,
  }

  const resetInfo = () => {
    setCreditCardInfo(defaultCreditCardInfo);
    setIsTaxExemptChecked(false);
    setTransactionItems([{key: 0, value: ""}, {key: 1, value: ""}, {key: 2, value: ""}, {key: 3, value: ""}])
    setTransactionId("0");
    setDefaultConfirmationEmail("");
    setPayoffAmount(BigNumber(0));
    setPayoffFairId(0);
    setPayoffFairInfo(defaultFairBarInfo);
    setRefundAmount(BigNumber(0));
    setCashAmountPaid(BigNumber(0));
    setSaleResponseInfo(defaultSaleResponseInfo);
  }

  // Display toaster message if user sends an email receipt on confirmation page
  const success = () => toast.success("Email Sent", {
    className: 'success-toast',
    position: "top-center",
    autoClose: 2000,
    hideProgressBar: true,
    theme: "colored",
    closeOnClick: true,
    draggable: true,
    progress: undefined,
    toastId: "success1",
  });

  // Hide reCAPTCHA badge on all pages except credit card pages
  useEffect(() => {
    if (document.getElementsByClassName(GRECAPTCHA)[0]) {
      if (currentPage === Pages.CreditCardReview || currentPage === Pages.CreditCardForm) {
        document.getElementsByClassName(GRECAPTCHA)[0].classList.remove("hidden");
      } else {
        document.getElementsByClassName(GRECAPTCHA)[0].classList.add("hidden");
      }
    }
  }, [currentPage]);

  let pageComponent = <></>
  switch(currentPage) {
    case Pages.Landing:
      pageComponent = <LandingPage setCurrentPage={setCurrentPage} fairId={fairId} bookfairInfo={bookfairInfo} setBookfairInfo={setBookfairInfo} loggedIn={loggedIn} setLoggedIn={setLoggedIn} modalDisplayed={modalDisplayed} setModalDisplayed={setModalDisplayed} />;
      break;
    case Pages.NewPurchase:
      pageComponent = 
        <Builder isFairEnded={bookfairInfo.isFairEnded} setCurrentPage={setCurrentPage} currentPage={currentPage}>
        <NewPurchaseContent 
          setCurrentPage={setCurrentPage} 
          resetInfo={resetInfo} 
          values={transactionItems} 
          setValues={setTransactionItems} 
          itemTotal={itemTotal} 
          tax={tax} 
          amountDue={amountDue} 
          isChecked={isTaxExemptChecked} 
          setIsChecked={setIsTaxExemptChecked} 
          taxStatus={bookfairInfo.taxStatus} 
          taxRate={bookfairInfo.taxRate} 
          taxExemptSalesAllowed={bookfairInfo.taxExemptSalesAllowed} 
          maxTransactionItems={bookfairInfo.maxTransactionItems}/>
          </Builder>
      break;
    case Pages.ProcessRefund:
      pageComponent = 
        <Builder isFairEnded={bookfairInfo.isFairEnded} setCurrentPage={setCurrentPage} currentPage={currentPage}>
        <ProcessRefundContent setCurrentPage={setCurrentPage} />
        </Builder>
      break;
    case Pages.SalesTotal:
      pageComponent = 
        <Builder 
          isFairEnded={bookfairInfo.isFairEnded} 
          setCurrentPage={setCurrentPage} 
          currentPage={currentPage}>
          <SalesTotalContent 
          setBookfairInfo={setBookfairInfo} 
          setCurrentPage={setCurrentPage} 
          bfInfo={bookfairInfo}/>
        </Builder>
      break;
    case Pages.PaymentMethod:
      pageComponent = 
        <PaymentMethodContent
          stfBalance={bookfairInfo.stfBalance} 
          setCurrentPage={setCurrentPage} 
          amountDue={paymentInfo.amountDue} 
          setBookfairInfo={setBookfairInfo} 
          setDefaultConfirmationEmail={setDefaultConfirmationEmail} 
          chairpersonEmail={bookfairInfo.chairpersonEmail}/>
      break
    case Pages.CreditCardForm:
      pageComponent = 
        <CreditCardForm 
          payoffFairInfo={payoffFairInfo} 
          setPayoffFairInfo={setPayoffFairInfo} 
          setCurrentPage={setCurrentPage} 
          ccInfo={creditCardInfo} 
          setCCInfo={setCreditCardInfo} 
          pInfo={paymentInfo} 
          payoffAmount={payoffAmount} 
          setPayoffAmount={setPayoffAmount} 
          version="sale" 
          loggedIn={loggedIn} 
          setLoggedIn={setLoggedIn} 
          payoffFairId={payoffFairId} 
          setPayoffFairId={setPayoffFairId}/>
      break;
    case Pages.CreditCardReview:
      pageComponent = 
        <CreditCardFinal 
          payoffFairInfo={payoffFairInfo} 
          setCurrentPage={setCurrentPage} 
          ccInfo={creditCardInfo} 
          pInfo={paymentInfo} 
          bfInfo={bookfairInfo} 
          setSaleResponseInfo={setSaleResponseInfo} 
          setBookfairInfo={setBookfairInfo} 
          setTransactionId={setTransactionId} 
          isTaxExemptChecked={isTaxExemptChecked} 
          version="sale" 
          payoffAmount={payoffAmount} 
          payoffFairId={payoffFairId}/>
      break;
    case Pages.Confirmation:
      pageComponent = 
        <PaymentConfirmationContent 
          success={success} 
          refundAmount={refundAmount} 
          setCurrentPage={setCurrentPage} 
          paymentInfo={paymentInfo} 
          resetInfo={resetInfo}
          defaultConfirmationEmail={defaultConfirmationEmail} 
          transactionId={transactionId} 
          version="sale" 
          payoffAmount={payoffAmount} 
          saleResponseInfo={saleResponseInfo} 
          cashAmountPaid={cashAmountPaid}/>
      break;
    case Pages.STFRedeem:
      pageComponent = 
        <STFRedeemContent 
          setCurrentPage={setCurrentPage} 
          pInfo={paymentInfo} 
          bfInfo={bookfairInfo} 
          setSaleResponseInfo={setSaleResponseInfo} 
          setBookfairInfo={setBookfairInfo} 
          isTaxExemptChecked={isTaxExemptChecked} 
          setTransactionId={setTransactionId}/>
      break;
    case Pages.eWallet:
      pageComponent = 
        <EwalletDisplayContent 
          setCurrentEWallet={setSelectedEWallet} 
          setCurrentPage={setCurrentPage} 
          isRefundPage={false}/>
      break;
    case Pages.eWalletReview:
      pageComponent = 
        <EwalletReviewContent 
          selectedEwallet={selectedEWallet} 
          setCurrentPage={setCurrentPage} 
          pInfo={paymentInfo} 
          bfInfo={bookfairInfo} 
          setBookfairInfo={setBookfairInfo} 
          isTaxExemptChecked={isTaxExemptChecked} 
          setDefaultConfirmationEmail={setDefaultConfirmationEmail} 
          setTransactionId={setTransactionId} 
          setSaleResponseInfo={setSaleResponseInfo} />
      break;
    case Pages.eWalletRefund:
      pageComponent = 
        <EwalletDisplayContent 
          setCurrentEWallet={setSelectedEWallet} 
          setCurrentPage={setCurrentPage} 
          isRefundPage={true}/>
      break;
    case Pages.eWalletRefundTransactions:
      pageComponent = 
        <EwalletChooseTransactionContent 
        selectedEwallet={selectedEWallet} 
        setSelectedTransaction={setSelectedTransaction} 
        setCurrentPage={setCurrentPage} />
      break;
    case Pages.eWalletRefundReview:
      pageComponent = 
        <EwalletRefundReviewContent 
          setDefaultConfirmationEmail={setDefaultConfirmationEmail} 
          setTransactionId={setTransactionId} 
          setRefundAmount={setRefundAmount} 
          transaction={selectedTransaction} 
          selectedEwallet={selectedEWallet} 
          setCurrentPage={setCurrentPage} />
      break;
    case Pages.POPayment:
      pageComponent = 
        <PurchaseOrderPaymentContent 
          pInfo={paymentInfo} 
          bfInfo={bookfairInfo} 
          isTaxExemptChecked={isTaxExemptChecked} 
          setSaleResponseInfo={setSaleResponseInfo} 
          setTransactionId={setTransactionId} 
          setBookfairInfo={setBookfairInfo} 
          setCurrentPage={setCurrentPage} />
      break;
    case Pages.CashPayment:
      pageComponent = 
        <CashPaymentContent 
          pInfo={paymentInfo} 
          bfInfo={bookfairInfo} 
          isTaxExemptChecked={isTaxExemptChecked} 
          setTransactionId={setTransactionId} 
          setSaleResponseInfo={setSaleResponseInfo} 
          setBookfairInfo={setBookfairInfo} 
          setCurrentPage={setCurrentPage} 
          setCashAmountPaid={setCashAmountPaid}/>
      break
    case Pages.Error:
      pageComponent = <ErrorPage />
      break;
    case Pages.NotAvailable:
      pageComponent = <NotAvailable />
      break;
    case Pages.ScholasticDollars:
      pageComponent = 
        <ScholasticDollars 
          pInfo={paymentInfo} 
          bfInfo={bookfairInfo} 
          isTaxExemptChecked={isTaxExemptChecked} 
          setSaleResponseInfo={setSaleResponseInfo} 
          setTransactionId={setTransactionId} 
          setBookfairInfo={setBookfairInfo} 
          setCurrentPage={setCurrentPage}></ScholasticDollars>
      break;
    case Pages.Checks:
      pageComponent = 
        <Checks 
          pInfo={paymentInfo} 
          bfInfo={bookfairInfo} 
          isTaxExemptChecked={isTaxExemptChecked} 
          setSaleResponseInfo={setSaleResponseInfo} 
          setBookfairInfo={setBookfairInfo} 
          setCurrentPage={setCurrentPage} 
          setTransactionId={setTransactionId}></Checks>
      break;
    default:
      break;
  }

  let pageComponentPayoff = <></>
  switch(currentPagePayoff) {
    case Pages.CreditCardForm:
      pageComponentPayoff = 
        <CreditCardForm 
          payoffFairInfo={payoffFairInfo} 
          setPayoffFairInfo={setPayoffFairInfo} 
          setCurrentPage={setCurrentPagePayoff} 
          ccInfo={creditCardInfo} 
          setCCInfo={setCreditCardInfo} 
          pInfo={paymentInfo} 
          payoffAmount={payoffAmount} 
          setPayoffAmount={setPayoffAmount} 
          version="payoff" 
          loggedIn={loggedIn} 
          setLoggedIn={setLoggedIn} 
          payoffFairId={payoffFairId} 
          setPayoffFairId={setPayoffFairId}/>
      break;
    case Pages.CreditCardReview:
      pageComponentPayoff = 
        <CreditCardFinal 
          payoffFairInfo={payoffFairInfo} 
          setCurrentPage={setCurrentPagePayoff} 
          ccInfo={creditCardInfo} 
          pInfo={paymentInfo} 
          bfInfo={bookfairInfo} 
          setBookfairInfo={setBookfairInfo} 
          setTransactionId={setTransactionId} 
          setSaleResponseInfo={setSaleResponseInfo} 
          isTaxExemptChecked={isTaxExemptChecked} 
          version="payoff" 
          payoffAmount={payoffAmount} 
          payoffFairId={payoffFairId}/>
      break;
    case Pages.Confirmation:
      pageComponentPayoff = 
        <PaymentConfirmationContent 
          success={success} 
          refundAmount={refundAmount} 
          setCurrentPage={setCurrentPagePayoff}
          paymentInfo={paymentInfo} 
          resetInfo={resetInfo} 
          defaultConfirmationEmail={defaultConfirmationEmail}
          transactionId={transactionId} 
          version="payoff" 
          payoffAmount={payoffAmount} 
          saleResponseInfo={saleResponseInfo} 
          cashAmountPaid={cashAmountPaid}/>
      break;
    case Pages.Error:
      pageComponentPayoff = <ErrorPage />;
      break;
    default:
      break;
  }

  return (
    <ConfigProvider>
      <ToastContainer />
      <Routes>
        <Route path="/" element={pageComponent} />
        <Route path="/creditcardpayment.html" element={pageComponentPayoff} />
        <Route path="/*" element={<RedirectPage />} />
      </Routes>
    </ConfigProvider>
  )
}

export default App;
